import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiService from "../services/api";

const FALLBACK_COLOR = "#0F2439";

function hexToRgb(hex) {
    const h = hex.replace("#", "");
    return { r: parseInt(h.substring(0, 2), 16), g: parseInt(h.substring(2, 4), 16), b: parseInt(h.substring(4, 6), 16) };
}

function makeBgColor(hex) {
    if (!hex) return "#f0f0f0";
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, 0.1)`;
}

const PHASES = [
    { key: "planning", label: "Planning" },
    { key: "setup", label: "Setup" },
    { key: "integration", label: "Integration" },
    { key: "launch-prep", label: "Launch prep" },
    { key: "live", label: "Live" },
];

const EMPTY_TIMELINE = { sections: [], updates: {}, checked: {} };

function deepClone(obj) { return JSON.parse(JSON.stringify(obj)); }

function useChecked(schoolId) {
    const [checked, setChecked] = useState({});
    useEffect(() => {
        try { const raw = localStorage.getItem(`checks:${schoolId}`); if (raw) setChecked(JSON.parse(raw)); } catch {}
    }, [schoolId]);
    const toggle = useCallback((weekIdx, actionIdx) => {
        setChecked((prev) => {
            const key = `${weekIdx}-${actionIdx}`;
            const next = { ...prev, [key]: !prev[key] };
            try { localStorage.setItem(`checks:${schoolId}`, JSON.stringify(next)); } catch {}
            return next;
        });
    }, [schoolId]);
    return [checked, toggle];
}

function EditableItem({ value, onSave, onDelete, accent, isSuperuser, children }) {
    const [editing, setEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const [hovered, setHovered] = useState(false);

    if (editing && isSuperuser) {
        return (
            <div style={{ display: "flex", gap: "6px", alignItems: "center", width: "100%" }}>
                <input
                    autoFocus
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && editValue.trim()) { onSave(editValue.trim()); setEditing(false); } if (e.key === "Escape") { setEditValue(value); setEditing(false); } }}
                    style={{ flex: 1, padding: "8px 12px", border: "1px solid #e8e8e8", borderRadius: "8px", fontSize: "14px", fontFamily: "'Baikal Book', sans-serif", outline: "none" }}
                />
                <button onClick={() => { if (editValue.trim()) { onSave(editValue.trim()); setEditing(false); } }} style={{ background: accent, color: "#fff", border: "none", borderRadius: "6px", padding: "6px 10px", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>Save</button>
                <button onClick={() => { setEditValue(value); setEditing(false); }} style={{ background: "none", border: "none", color: "#999", fontSize: "12px", cursor: "pointer" }}>Cancel</button>
            </div>
        );
    }

    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{ display: "flex", alignItems: "center", gap: "6px", width: "100%" }}
        >
            <div style={{ flex: 1 }}>{children}</div>
            {isSuperuser && hovered && (
                <div style={{ display: "flex", gap: "2px", flexShrink: 0 }}>
                    <button onClick={(e) => { e.stopPropagation(); setEditing(true); }} title="Edit" style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", color: "#999", display: "flex" }}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M10.08 1.92a1.5 1.5 0 012.12 2.12L5.13 11.1 2 12l.9-3.13L10.08 1.92z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Remove" style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", color: "#cc3333", display: "flex" }}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3.5 3.5l7 7M10.5 3.5l-7 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                    </button>
                </div>
            )}
        </div>
    );
}

function EditableUpdate({ update, onSave, onDelete, accent, isSuperuser }) {
    const [editing, setEditing] = useState(false);
    const [date, setDate] = useState(update.date);
    const [text, setText] = useState(update.text);
    const [hovered, setHovered] = useState(false);
    const inputStyle = { width: "100%", padding: "8px 12px", border: "1px solid #e8e8e8", borderRadius: "8px", fontSize: "14px", fontFamily: "'Baikal Book', sans-serif", outline: "none", resize: "none" };

    if (editing && isSuperuser) {
        return (
            <div style={{ padding: "12px 16px", border: "1px solid #eee", borderRadius: "10px", display: "flex", flexDirection: "column", gap: "8px" }}>
                <input value={date} onChange={e => setDate(e.target.value)} placeholder="Date label" style={inputStyle} />
                <textarea rows={2} value={text} onChange={e => setText(e.target.value)} placeholder="Update text..." style={inputStyle} />
                <div style={{ display: "flex", gap: "8px" }}>
                    <button onClick={() => { if (date.trim() && text.trim()) { onSave({ date: date.trim(), text: text.trim() }); setEditing(false); } }} style={{ background: accent, color: "#fff", border: "none", borderRadius: "6px", padding: "6px 10px", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>Save</button>
                    <button onClick={() => { setDate(update.date); setText(update.text); setEditing(false); }} style={{ background: "none", border: "none", color: "#999", fontSize: "12px", cursor: "pointer" }}>Cancel</button>
                </div>
            </div>
        );
    }

    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{ display: "flex", alignItems: "flex-start", gap: "6px" }}
        >
            <div style={{ flex: 1, paddingLeft: "14px", borderLeft: "2px solid #e0e0e0" }}>
                <div style={{ fontSize: "11px", color: "#bbb", marginBottom: "3px", fontWeight: 500 }}>{update.date}</div>
                <div style={{ fontSize: "14px", color: "#555", lineHeight: 1.6 }}>{update.text}</div>
            </div>
            {isSuperuser && hovered && (
                <div style={{ display: "flex", gap: "2px", flexShrink: 0, marginTop: "2px" }}>
                    <button onClick={() => setEditing(true)} title="Edit" style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", color: "#999", display: "flex" }}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M10.08 1.92a1.5 1.5 0 012.12 2.12L5.13 11.1 2 12l.9-3.13L10.08 1.92z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                    <button onClick={() => onDelete()} title="Remove" style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", color: "#cc3333", display: "flex" }}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3.5 3.5l7 7M10.5 3.5l-7 7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                    </button>
                </div>
            )}
        </div>
    );
}

function AddButton({ onClick, accent }) {
    const [hovered, setHovered] = useState(false);
    return (
        <button
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            title="Add"
            style={{
                width: 26, height: 26, minWidth: 26, borderRadius: "50%",
                border: `1.5px solid ${hovered ? accent : accent + "44"}`,
                background: hovered ? makeBgColor(accent) : "transparent",
                color: accent, fontSize: "18px", fontWeight: 600, lineHeight: 1,
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", transition: "all 0.2s ease", padding: 0,
            }}
        >+</button>
    );
}

function InlineInput({ placeholder, onSubmit, onCancel, accent, multiline }) {
    const [value, setValue] = useState("");
    const handleSubmit = () => { if (value.trim()) { onSubmit(value.trim()); setValue(""); } };
    const inputStyle = {
        width: "100%", padding: "8px 12px", border: "1px solid #e8e8e8", borderRadius: "8px",
        fontSize: "14px", fontFamily: "'Baikal Book', sans-serif", outline: "none", resize: "none",
    };
    return (
        <div style={{ padding: "12px 16px", border: "1px solid #eee", borderRadius: "10px", display: "flex", flexDirection: "column", gap: "8px" }}>
            {multiline ? (
                <textarea rows={2} placeholder={placeholder} value={value} onChange={e => setValue(e.target.value)} style={inputStyle} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }} />
            ) : (
                <input placeholder={placeholder} value={value} onChange={e => setValue(e.target.value)} style={inputStyle} onKeyDown={e => { if (e.key === "Enter") handleSubmit(); }} />
            )}
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <button onClick={handleSubmit} style={{ background: accent, color: "#fff", border: "none", borderRadius: "8px", padding: "6px 14px", fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "'Baikal Book', sans-serif" }}>Add</button>
                <button onClick={onCancel} style={{ background: "none", border: "none", color: "#999", fontSize: "13px", cursor: "pointer", fontFamily: "'Baikal Book', sans-serif" }}>Cancel</button>
            </div>
        </div>
    );
}

const SectionButton = ({ label, isActive, accent, accentBg, isDragging, isSuperuser, canDelete, onClick, onEdit, onDelete, draggable, onDragStart, onDragEnd, setRef }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <div
            ref={setRef}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{ position: "relative", display: "inline-flex" }}
        >
            <button
                onClick={onClick}
                draggable={draggable}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                style={{
                    minWidth: "88px", padding: "8px 14px", border: "1px solid",
                    borderColor: isActive ? accent : "#e8e8e8",
                    borderRadius: "10px",
                    background: isActive ? accentBg : "transparent",
                    color: isActive ? accent : "#888",
                    fontSize: "13px", fontWeight: isActive ? 600 : 400,
                    cursor: isSuperuser ? "grab" : "pointer",
                    whiteSpace: "nowrap",
                    fontFamily: "'Baikal Book', sans-serif",
                    opacity: isDragging ? 0.3 : 1,
                    transition: "opacity 0.15s ease",
                }}
            >
                {label}
            </button>
            {isSuperuser && hovered && !isDragging && (
                <div style={{ position: "absolute", top: "-8px", right: "-8px", display: "flex", gap: "2px" }}>
                    <button onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Edit" style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", border: "1px solid #ddd", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0, color: "#999" }}>
                        <svg width="9" height="9" viewBox="0 0 14 14" fill="none"><path d="M10.08 1.92a1.5 1.5 0 012.12 2.12L5.13 11.1 2 12l.9-3.13L10.08 1.92z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                    {canDelete && (
                        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Remove" style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", border: "1px solid #ddd", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0, color: "#cc3333" }}>
                            <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

function EditSectionForm({ label: initLabel, phase: initPhase, onSave, onCancel, accent }) {
    const [label, setLabel] = useState(initLabel);
    const [phase, setPhase] = useState(initPhase);
    const inputStyle = {
        width: "100%", padding: "8px 12px", border: "1px solid #e8e8e8", borderRadius: "8px",
        fontSize: "14px", fontFamily: "'Baikal Book', sans-serif", outline: "none",
    };
    return (
        <div style={{ padding: "12px 16px", border: "1px solid #eee", borderRadius: "10px", display: "flex", flexDirection: "column", gap: "8px", marginBottom: "8px" }}>
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="Label" style={inputStyle} onKeyDown={e => { if (e.key === "Enter" && label.trim()) onSave({ label: label.trim(), phase }); }} />
            <select value={phase} onChange={e => setPhase(Number(e.target.value))} style={{ ...inputStyle, appearance: "auto" }}>
                {PHASES.map((p, i) => <option key={p.key} value={i}>{p.label}</option>)}
            </select>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <button onClick={() => { if (label.trim()) onSave({ label: label.trim(), phase }); }} style={{ background: accent, color: "#fff", border: "none", borderRadius: "8px", padding: "6px 14px", fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "'Baikal Book', sans-serif" }}>Save</button>
                <button onClick={onCancel} style={{ background: "none", border: "none", color: "#999", fontSize: "13px", cursor: "pointer", fontFamily: "'Baikal Book', sans-serif" }}>Cancel</button>
            </div>
        </div>
    );
}

function AddWeekForm({ onSubmit, onCancel, accent }) {
    const [label, setLabel] = useState("");
    const [phase, setPhase] = useState(0);
    const inputStyle = {
        width: "100%", padding: "8px 12px", border: "1px solid #e8e8e8", borderRadius: "8px",
        fontSize: "14px", fontFamily: "'Baikal Book', sans-serif", outline: "none",
    };
    return (
        <div style={{ padding: "12px 16px", border: "1px solid #eee", borderRadius: "10px", display: "flex", flexDirection: "column", gap: "8px", marginBottom: "8px" }}>
            <input placeholder="Label (e.g. May 18–22)" value={label} onChange={e => setLabel(e.target.value)} style={inputStyle} />
            <select value={phase} onChange={e => setPhase(Number(e.target.value))} style={{ ...inputStyle, appearance: "auto" }}>
                {PHASES.map((p, i) => <option key={p.key} value={i}>{p.label}</option>)}
            </select>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <button onClick={() => { if (label.trim()) onSubmit({ label: label.trim(), phase }); }} style={{ background: accent, color: "#fff", border: "none", borderRadius: "8px", padding: "6px 14px", fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "'Baikal Book', sans-serif" }}>Add section</button>
                <button onClick={onCancel} style={{ background: "none", border: "none", color: "#999", fontSize: "13px", cursor: "pointer", fontFamily: "'Baikal Book', sans-serif" }}>Cancel</button>
            </div>
        </div>
    );
}

function AddUpdateForm({ onSubmit, onCancel, accent }) {
    const [date, setDate] = useState("");
    const [text, setText] = useState("");
    const inputStyle = {
        width: "100%", padding: "8px 12px", border: "1px solid #e8e8e8", borderRadius: "8px",
        fontSize: "14px", fontFamily: "'Baikal Book', sans-serif", outline: "none", resize: "none",
    };
    return (
        <div style={{ padding: "12px 16px", border: "1px solid #eee", borderRadius: "10px", display: "flex", flexDirection: "column", gap: "8px" }}>
            <input placeholder="Date label (e.g. Apr 10)" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
            <textarea rows={2} placeholder="Update text..." value={text} onChange={e => setText(e.target.value)} style={inputStyle} />
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <button onClick={() => { if (date.trim() && text.trim()) onSubmit({ date: date.trim(), text: text.trim() }); }} style={{ background: accent, color: "#fff", border: "none", borderRadius: "8px", padding: "6px 14px", fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "'Baikal Book', sans-serif" }}>Add</button>
                <button onClick={onCancel} style={{ background: "none", border: "none", color: "#999", fontSize: "13px", cursor: "pointer", fontFamily: "'Baikal Book', sans-serif" }}>Cancel</button>
            </div>
        </div>
    );
}

export default function OnboardingPage() {
    const { schoolId } = useParams();
    const navigate = useNavigate();
    const [school, setSchool] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [weekIdx, setWeekIdx] = useState(0);
    const [isSuperuser, setIsSuperuser] = useState(false);
    const [localData, setLocalData] = useState(null);
    const [showAddWeek, setShowAddWeek] = useState(false);
    const [showAddTask, setShowAddTask] = useState(false);
    const [showAddAction, setShowAddAction] = useState(false);
    const [showAddUpdate, setShowAddUpdate] = useState(false);
    const [editingSectionIdx, setEditingSectionIdx] = useState(null);
    const [dragIdx, setDragIdx] = useState(null);
    const [dropIdx, setDropIdx] = useState(null);
    const weekRefs = useRef([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await apiService.getOnboardingTimeline(schoolId);
                setSchool(res.school);
                const timeline = res.timeline;
                setLocalData(deepClone(timeline && timeline.sections ? timeline : EMPTY_TIMELINE));
            } catch {
                setError("School not found.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [schoolId]);

    useEffect(() => {
        const checkSuperuser = async () => {
            const token = localStorage.getItem("access_token");
            if (!token) return;
            try {
                const user = await apiService.fetchCurrentUser();
                if (user?.is_superuser) setIsSuperuser(true);
            } catch {}
        };
        checkSuperuser();
    }, []);

    // Checked state: prefer localData.checked (from API), fall back to localStorage
    const [lsChecked, lsToggle] = useChecked(schoolId);
    const checked = localData?.checked || lsChecked;
    const toggle = useCallback((wi, ai) => {
        const key = `${wi}-${ai}`;
        const newVal = !checked[key];
        // Update local state
        setLocalData(prev => {
            if (!prev) return prev;
            const next = deepClone(prev);
            if (!next.checked) next.checked = {};
            next.checked[key] = newVal;
            return next;
        });
        // Also update localStorage as fallback
        lsToggle(wi, ai);
        // Persist to API
        apiService.toggleOnboardingAction(schoolId, key, newVal).catch(() => {});
    }, [checked, schoolId, lsToggle]);

    // Auto-save timeline to API when localData changes (superuser only, debounced)
    const saveTimeoutRef = useRef(null);
    useEffect(() => {
        if (!isSuperuser || !localData || loading) return;
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => {
            apiService.updateOnboardingTimeline(schoolId, localData).catch((err) => {
                console.error("Failed to save timeline:", err);
            });
        }, 800);
        return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
    }, [localData, isSuperuser, schoolId, loading]);

    const accent = school?.primary_color || FALLBACK_COLOR;
    const accentBg = makeBgColor(accent);

    if (loading || !localData) {
        return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", fontFamily: "'Baikal Book', sans-serif", color: "#999" }}>Loading...</div>;
    }
    if (error || !school) {
        return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", fontFamily: "'Baikal Book', sans-serif", color: "#cc3333" }}>{error || "School not found."}</div>;
    }

    const hasSections = localData.sections && localData.sections.length > 0;
    const week = hasSections ? (localData.sections[weekIdx] || localData.sections[0]) : null;
    const totalActions = hasSections ? localData.sections.reduce((sum, w) => sum + (w.actions?.length || 0), 0) : 0;
    const completedActions = hasSections ? localData.sections.reduce((sum, w, wi) => sum + (w.actions || []).filter((_, ai) => checked[`${wi}-${ai}`]).length, 0) : 0;
    const weekDone = week ? week.actions.filter((_, ai) => checked[`${weekIdx}-${ai}`]).length : 0;
    const weekComplete = week ? weekDone === week.actions.length : false;
    const updates = localData.updates[weekIdx] || [];

    const addWeek = ({ label, phase }) => {
        setLocalData(prev => {
            const next = deepClone(prev);
            next.sections.push({ label, phase, tasks: [], actions: [] });
            next.updates[next.sections.length - 1] = [];
            return next;
        });
        setWeekIdx(localData.sections.length);
        setShowAddWeek(false);
    };
    const addTask = (text) => {
        setLocalData(prev => {
            const next = deepClone(prev);
            next.sections[weekIdx].tasks.push(text);
            return next;
        });
        setShowAddTask(false);
    };
    const addAction = (text) => {
        setLocalData(prev => {
            const next = deepClone(prev);
            next.sections[weekIdx].actions.push(text);
            return next;
        });
        setShowAddAction(false);
    };
    const addUpdate = ({ date, text }) => {
        setLocalData(prev => {
            const next = deepClone(prev);
            if (!next.updates[weekIdx]) next.updates[weekIdx] = [];
            next.updates[weekIdx].push({ date, text });
            return next;
        });
        setShowAddUpdate(false);
    };

    const editTask = (i, text) => { setLocalData(prev => { const next = deepClone(prev); next.sections[weekIdx].tasks[i] = text; return next; }); };
    const deleteTask = (i) => { setLocalData(prev => { const next = deepClone(prev); next.sections[weekIdx].tasks.splice(i, 1); return next; }); };
    const editAction = (i, text) => { setLocalData(prev => { const next = deepClone(prev); next.sections[weekIdx].actions[i] = text; return next; }); };
    const deleteAction = (i) => { setLocalData(prev => { const next = deepClone(prev); next.sections[weekIdx].actions.splice(i, 1); return next; }); };
    const editUpdate = (i, upd) => { setLocalData(prev => { const next = deepClone(prev); next.updates[weekIdx][i] = upd; return next; }); };
    const deleteUpdate = (i) => { setLocalData(prev => { const next = deepClone(prev); next.updates[weekIdx].splice(i, 1); return next; }); };
    const editSection = (i, { label, phase }) => { setLocalData(prev => { const next = deepClone(prev); next.sections[i].label = label; next.sections[i].phase = phase; return next; }); };
    const deleteSection = (i) => {
        setLocalData(prev => {
            const next = deepClone(prev);
            next.sections.splice(i, 1);
            const oldUpdates = deepClone(prev.updates);
            const newUpdates = {};
            let newI = 0;
            for (let oldI = 0; oldI < prev.sections.length; oldI++) {
                if (oldI === i) continue;
                if (oldUpdates[oldI]) newUpdates[newI] = oldUpdates[oldI];
                newI++;
            }
            next.updates = newUpdates;
            return next;
        });
        if (weekIdx >= localData.sections.length - 1) setWeekIdx(Math.max(0, localData.sections.length - 2));
        else if (weekIdx > i) setWeekIdx(weekIdx - 1);
    };

    const handleContainerDragOver = (e) => {
        e.preventDefault();
        if (dragIdx === null) return;
        const x = e.clientX;
        let closest = null;
        let closestDist = Infinity;
        weekRefs.current.forEach((el, i) => {
            if (!el || i === dragIdx) return;
            const rect = el.getBoundingClientRect();
            const center = rect.left + rect.width / 2;
            const dist = Math.abs(x - center);
            if (dist < closestDist) { closestDist = dist; closest = x < center ? i : i + 1; }
        });
        if (closest !== null && closest !== dropIdx) setDropIdx(closest);
    };

    const handleWeekDrop = () => {
        if (dragIdx === null || dropIdx === null) { setDragIdx(null); setDropIdx(null); return; }
        const fromIdx = dragIdx;
        let toIdx = dropIdx > fromIdx ? dropIdx - 1 : dropIdx;
        if (fromIdx === toIdx) { setDragIdx(null); setDropIdx(null); return; }
        setLocalData(prev => {
            const next = deepClone(prev);
            const [moved] = next.sections.splice(fromIdx, 1);
            next.sections.splice(toIdx, 0, moved);
            const oldUpdates = deepClone(prev.updates);
            const newUpdates = {};
            const order = Array.from({ length: prev.sections.length }, (_, i) => i);
            const [movedI] = order.splice(fromIdx, 1);
            order.splice(toIdx, 0, movedI);
            order.forEach((oldI, newI) => { if (oldUpdates[oldI]) newUpdates[newI] = oldUpdates[oldI]; });
            next.updates = newUpdates;
            return next;
        });
        if (weekIdx === fromIdx) setWeekIdx(toIdx);
        else if (fromIdx < toIdx && weekIdx > fromIdx && weekIdx <= toIdx) setWeekIdx(weekIdx - 1);
        else if (fromIdx > toIdx && weekIdx < fromIdx && weekIdx >= toIdx) setWeekIdx(weekIdx + 1);
        setDragIdx(null);
        setDropIdx(null);
    };

    return (
        <div style={{ minHeight: "100vh", background: "#fff", fontFamily: "'Baikal Book', sans-serif" }}>
            <div style={{ maxWidth: 680, margin: "0 auto", padding: "1.5rem 1rem 3rem" }}>

                {/* Back + logo */}
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "2rem" }}>
                    <button onClick={() => navigate(`/onboarding/${schoolId}/`)} style={{ background: "none", border: "none", cursor: "pointer", padding: "6px", color: "#999", fontSize: "14px", display: "flex", alignItems: "center", gap: "4px", fontFamily: "'Baikal Book', sans-serif" }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        Back
                    </button>
                    <div style={{ flex: 1 }} />
                    {school.image && <img src={school.image} alt={school.name} style={{ width: 32, height: 32, borderRadius: "8px", objectFit: "contain" }} />}
                </div>

                {/* Title */}
                <h1 style={{ fontFamily: "'Baikal Condensed', sans-serif", fontSize: "clamp(24px, 4vw, 34px)", fontWeight: 900, margin: "0 0 0.25rem", color: "#1a1a1a", letterSpacing: "-0.3px" }}>{school.name}</h1>
                <p style={{ fontSize: "15px", color: "#888", margin: "0 0 2rem" }}>Onboarding progress</p>

                {/* Progress bar */}
                <div style={{ marginBottom: "2rem" }}>
                    <div style={{ display: "flex", gap: "3px", marginBottom: "8px" }}>
                        {PHASES.map((p, i) => (
                            <div key={p.key} style={{ flex: 1, height: "5px", borderRadius: i === 0 ? "3px 0 0 3px" : i === 4 ? "0 3px 3px 0" : "0", background: i <= (week?.phase ?? -1) ? accent : "#eee", transition: "background 0.3s ease" }} />
                        ))}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        {PHASES.map((p, i) => (
                            <span key={p.key} style={{ fontSize: "11px", fontWeight: i === (week?.phase ?? -1) ? 600 : 400, color: i === (week?.phase ?? -1) ? accent : "#bbb", transition: "color 0.3s ease" }}>{p.label}</span>
                        ))}
                    </div>
                </div>

                {/* Week buttons */}
                <div
                    onDragOver={isSuperuser ? handleContainerDragOver : undefined}
                    onDrop={isSuperuser ? (e) => { e.preventDefault(); handleWeekDrop(); } : undefined}
                    style={{ display: "flex", gap: "6px", overflowX: "auto", marginBottom: "2rem", paddingTop: "12px", paddingBottom: "4px", alignItems: "center" }}
                >
                    {localData.sections.map((w, i) => {
                        const showPlaceholder = dragIdx !== null && dropIdx === i && dragIdx !== i;
                        return (
                            <div key={i} style={{ display: "contents" }}>
                                {showPlaceholder && (
                                    <div style={{ minWidth: "88px", padding: "8px 14px", border: "2px dashed " + accent + "55", borderRadius: "10px", background: accentBg, fontSize: "13px", color: accent + "88", fontFamily: "'Baikal Book', sans-serif", textAlign: "center", whiteSpace: "nowrap" }}>
                                        {localData.sections[dragIdx]?.label}
                                    </div>
                                )}
                                <SectionButton
                                    setRef={el => weekRefs.current[i] = el}
                                    label={w.label}
                                    isActive={i === weekIdx}
                                    accent={accent}
                                    accentBg={accentBg}
                                    isDragging={dragIdx === i}
                                    isSuperuser={isSuperuser}
                                    canDelete={localData.sections.length > 1}
                                    onClick={() => { if (dragIdx === null) setWeekIdx(i); }}
                                    onEdit={() => setEditingSectionIdx(i)}
                                    onDelete={() => deleteSection(i)}
                                    draggable={isSuperuser}
                                    onDragStart={isSuperuser ? (e) => { setDragIdx(i); setDropIdx(null); e.dataTransfer.effectAllowed = "move"; } : undefined}
                                    onDragEnd={isSuperuser ? () => { setDragIdx(null); setDropIdx(null); } : undefined}
                                />
                            </div>
                        );
                    })}
                    {/* Placeholder at end */}
                    {dragIdx !== null && dropIdx === localData.sections.length && (
                        <div style={{ minWidth: "88px", padding: "8px 14px", border: "2px dashed " + accent + "55", borderRadius: "10px", background: accentBg, fontSize: "13px", color: accent + "88", fontFamily: "'Baikal Book', sans-serif", textAlign: "center", whiteSpace: "nowrap" }}>
                            {localData.sections[dragIdx]?.label}
                        </div>
                    )}
                    {isSuperuser && <AddButton onClick={() => setShowAddWeek(!showAddWeek)} accent={accent} />}
                </div>
                {showAddWeek && isSuperuser && <AddWeekForm accent={accent} onSubmit={addWeek} onCancel={() => setShowAddWeek(false)} />}
                {editingSectionIdx !== null && isSuperuser && (() => {
                    const s = localData.sections[editingSectionIdx];
                    return <EditSectionForm label={s.label} phase={s.phase} accent={accent} onSave={({ label, phase }) => { editSection(editingSectionIdx, { label, phase }); setEditingSectionIdx(null); }} onCancel={() => setEditingSectionIdx(null)} />;
                })()}

                {week && <>
                {/* Stats */}
                <div style={{ display: "flex", gap: "12px", marginBottom: "2rem" }}>
                    <div style={{ flex: 1, padding: "16px 18px", background: "#f8f8f6", borderRadius: "12px" }}>
                        <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>This week</div>
                        <div style={{ fontSize: "22px", fontWeight: 600, color: weekComplete ? "#2D7A50" : "#1a1a1a" }}>{weekComplete ? "All done" : `${weekDone} / ${week.actions.length}`}</div>
                    </div>
                    <div style={{ flex: 1, padding: "16px 18px", background: "#f8f8f6", borderRadius: "12px" }}>
                        <div style={{ fontSize: "12px", color: "#999", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Overall</div>
                        <div style={{ fontSize: "22px", fontWeight: 600, color: "#1a1a1a" }}>{totalActions > 0 ? Math.round((completedActions / totalActions) * 100) : 0}%</div>
                    </div>
                </div>

                {/* What we're working on */}
                <section style={{ marginBottom: "2rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                        <h2 style={{ fontSize: "13px", fontWeight: 600, color: "#999", textTransform: "uppercase", letterSpacing: "0.6px", margin: 0 }}>What we're working on</h2>
                        {isSuperuser && <AddButton onClick={() => setShowAddTask(!showAddTask)} accent={accent} />}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        {week.tasks.map((t, i) => (
                            <div key={i} style={{ padding: "12px 16px", borderRadius: "10px", border: "1px solid #eee", fontSize: "14px", color: "#444", lineHeight: 1.5 }}>
                                <EditableItem value={t} onSave={(v) => editTask(i, v)} onDelete={() => deleteTask(i)} accent={accent} isSuperuser={isSuperuser}>
                                    {t}
                                </EditableItem>
                            </div>
                        ))}
                        {showAddTask && isSuperuser && <InlineInput placeholder="Add a task..." accent={accent} onSubmit={addTask} onCancel={() => setShowAddTask(false)} />}
                    </div>
                </section>

                {/* Your action items */}
                <section style={{ marginBottom: "2rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                        <h2 style={{ fontSize: "13px", fontWeight: 600, color: accent, textTransform: "uppercase", letterSpacing: "0.6px", margin: 0 }}>Your action items</h2>
                        {isSuperuser && <AddButton onClick={() => setShowAddAction(!showAddAction)} accent={accent} />}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        {week.actions.map((a, ai) => {
                            const done = checked[`${weekIdx}-${ai}`];
                            return (
                                <div key={ai} style={{ display: "flex", alignItems: "flex-start", gap: "12px", padding: "12px 16px", borderRadius: "10px", border: "1px solid", borderColor: done ? "#e0e0e0" : accent + "33", background: done ? "#fafafa" : accentBg + "88", transition: "all 0.2s ease", fontFamily: "'Baikal Book', sans-serif", width: "100%" }}>
                                    <div onClick={() => toggle(weekIdx, ai)} style={{ width: 20, height: 20, minWidth: 20, borderRadius: "6px", border: done ? "none" : `1.5px solid ${accent}`, background: done ? accent : "transparent", display: "flex", alignItems: "center", justifyContent: "center", marginTop: "1px", transition: "all 0.2s ease", cursor: "pointer" }}>
                                        {done && <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                    </div>
                                    <EditableItem value={a} onSave={(v) => editAction(ai, v)} onDelete={() => deleteAction(ai)} accent={accent} isSuperuser={isSuperuser}>
                                        <span onClick={() => toggle(weekIdx, ai)} style={{ fontSize: "14px", color: done ? "#bbb" : "#333", textDecoration: done ? "line-through" : "none", lineHeight: 1.5, transition: "color 0.2s ease", cursor: "pointer" }}>{a}</span>
                                    </EditableItem>
                                </div>
                            );
                        })}
                        {showAddAction && isSuperuser && <InlineInput placeholder="Add an action item..." accent={accent} onSubmit={addAction} onCancel={() => setShowAddAction(false)} />}
                    </div>
                </section>

                {/* Updates */}
                <section>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                        <h2 style={{ fontSize: "13px", fontWeight: 600, color: "#999", textTransform: "uppercase", letterSpacing: "0.6px", margin: 0 }}>Updates</h2>
                        {isSuperuser && <AddButton onClick={() => setShowAddUpdate(!showAddUpdate)} accent={accent} />}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        {updates.map((u, i) => (
                            <EditableUpdate key={i} update={u} onSave={(upd) => editUpdate(i, upd)} onDelete={() => deleteUpdate(i)} accent={accent} isSuperuser={isSuperuser} />
                        ))}
                        {showAddUpdate && isSuperuser && <AddUpdateForm accent={accent} onSubmit={addUpdate} onCancel={() => setShowAddUpdate(false)} />}
                    </div>
                </section>

                </>}
                {/* Footer */}
                <div style={{ marginTop: "3rem", paddingTop: "1.5rem", borderTop: "1px solid #eee", textAlign: "center", fontSize: "13px", color: "#ccc" }}>
                    Questions? Reach out to your onboarding contact anytime.
                </div>
            </div>
        </div>
    );
}
